import React, {useEffect, useRef, useState} from "react"
import classNames from "classnames"
import {isWirePayment, WirePayment} from "../../resources/payment"
import {GridContainer} from "../../containers/GridContainer/GridContainer"
import {AssignmentNavigator} from "../../components/AssignmentNavigator/AssignmentNavigator"
import {useQueryState} from "use-location-state"
import {AssignmentsQueueHeader} from "./AssignmentsQueue/AssignmentsQueueHeader"
import {CollapsedAssignmentQueue} from "./AssignmentsQueue/CollapsedAssignmentQueue"
import {AssignmentPageItemCallback} from "../../components/AssignmentPageItemCallback/AssignmentPageItemCallback"
import {Resource} from "../../resources/common"
import {AssignmentItem, AssignmentItemId, AssignmentStage, AssignmentType} from "../../resources/assignments"
import AssignmentsEmptyStateGraphics from "./AssignmentsEmptyStateGraphics"
import {Customer} from "../../resources/customer"
import {AssignmentPageItemCallbackReview} from "../../components/AssignmentPageItemCallbackReview/AssignmentPageItemCallbackReview"
import {AssignmentPageItemOFAC} from "../../components/AssignmentPageItemOFAC/AssignmentPageItemOFAC"
import {AssignmentPayload} from "./Assignments"
import {Claims} from "../../services/auth"
import {AssignmentPageItemGeneralReview} from "../../components/AssignmentPageItemGeneralReview/AssignmentPageItemGeneralReview"
import {Account, isDepositAccount} from "../../resources/account"
import {AssignmentPageItemDrawdownAuthorizationReview} from "../../components/AssignmentPageItemDrawdownAuthorizationReview/AssignmentPageItemDrawdownAuthorizationReview"
import {WireDrawdown} from "../../resources/wireDrawdown"
import {AssignmentPageItemGeneralReviewCheckDeposit} from "../../components/AssignmentPageItemGeneralReviewCheckDeposit/AssignmentPageItemGeneralReviewCheckDeposit"
import {CheckDeposit} from "../../resources/checkDeposits"
import {AssignmentPageItemNameMismatch} from "../../components/AssignmentPageItemNameMismatch/AssignmentPageItemNameMismatch"
import {WireReceivedPayment} from "../../resources/receivedPayments"
import {getIncomingWireFromIncluded} from "../../resources/incomingWire"
import {useLocation, useNavigate} from "react-router-dom"
import {pageFallbackPath, useListReturnPath} from "../../hooks/useListReturnPath"

interface AssignmentsViewProps {
	resource: Resource
	included?: Resource[]
	payload?: AssignmentPayload
	assignments: AssignmentItem[]
	accessToken: string
	refresh: () => void
	refreshToken: number
	claims?: Claims
	isCollapsibleQueue?: boolean
	defaultCollapsed?: boolean
}

export const getAssignmentIsDisabled = (stage?: AssignmentStage, isOwnersItem?: boolean) =>
	stage === AssignmentStage.Completed || !isOwnersItem

const getPaymentCustomerAndBusinessApplication = (
	resource: Resource,
	included?: Resource[],
	payload?: AssignmentPayload
) => {
	const customer = included?.find((resource) =>
		["individualCustomer", "businessCustomer", "businessFBOCustomer", "individualWalletCustomer"].includes(
			resource.type
		)
	) as Customer
	const businessApplication = payload?.businessApplication
	return {customer, businessApplication}
}

const AssignmentPageItemView = ({
	currentAssignmentItem,
	resource,
	included,
	payload,
	accessToken,
	refresh,
	refreshToken,
	claims,
	onFinalActionSuccess,
}: {
	currentAssignmentItem: AssignmentItem
	resource: Resource
	included?: Resource[]
	payload?: AssignmentPayload
	accessToken: string
	refresh: () => void
	refreshToken: number
	claims?: Claims
	onFinalActionSuccess?: () => void
}) => {
	if (!currentAssignmentItem) {
		return <div>No assignment</div>
	}
	const commonProps = {
		disabled: getAssignmentIsDisabled(currentAssignmentItem.stage, currentAssignmentItem.isOwnersItem),
		accessToken,
		refresh,
		refreshToken,
	}
	switch (currentAssignmentItem.type) {
		case AssignmentType.callbackAssignment: {
			const {customer, businessApplication} = getPaymentCustomerAndBusinessApplication(resource, included, payload)
			return (
				<AssignmentPageItemCallback
					{...currentAssignmentItem}
					{...commonProps}
					wire={resource as WirePayment}
					customer={customer}
					businessApplication={businessApplication}
					claims={claims}
				/>
			)
		}
		case AssignmentType.callbackReviewAssignment: {
			const {customer, businessApplication} = getPaymentCustomerAndBusinessApplication(resource, included, payload)
			return (
				<AssignmentPageItemCallbackReview
					{...currentAssignmentItem}
					{...commonProps}
					wire={resource as WirePayment}
					customer={customer}
					businessApplication={businessApplication}
					claims={claims}
				/>
			)
		}
		case AssignmentType.ofacCheckAssignment: {
			const incomingWire = getIncomingWireFromIncluded(included)
			const wirePayment = isWirePayment(resource) ? resource : undefined
			const counterparty = incomingWire?.attributes.counterparty || wirePayment?.attributes.counterparty

			return <AssignmentPageItemOFAC {...currentAssignmentItem} {...commonProps} counterparty={counterparty} />
		}
		case AssignmentType.generalReviewAssignment: {
			const {customer, businessApplication} = getPaymentCustomerAndBusinessApplication(resource, included, payload)
			const account = included && (included.find((i) => isDepositAccount(i)) as Account)

			switch (resource.type.toString()) {
				case "checkDeposit":
					return (
						<AssignmentPageItemGeneralReviewCheckDeposit
							{...currentAssignmentItem}
							{...commonProps}
							checkDeposit={resource as CheckDeposit}
							included={included}
							claims={claims}
							frontImage={payload?.frontImage}
							backImage={payload?.backImage}
							frontTiffImage={payload?.frontTiffImage}
							backTiffImage={payload?.backTiffImage}
							expectsTiff={payload?.expectsTiff}
							onFinalActionSuccess={onFinalActionSuccess}
						/>
					)
				default:
					return (
						<AssignmentPageItemGeneralReview
							{...currentAssignmentItem}
							{...commonProps}
							wire={resource as WirePayment}
							account={account}
							customer={customer}
							businessApplication={businessApplication}
							claims={claims}
						/>
					)
			}
		}
		case AssignmentType.drawdownAuthorizationReviewAssignment: {
			const {customer, businessApplication} = getPaymentCustomerAndBusinessApplication(resource, included, payload)
			const account = included && (included.find((i) => isDepositAccount(i)) as Account)
			const wireDrawdown = included && (included.find((i) => i.type === "wireDrawdown") as WireDrawdown)

			return (
				<AssignmentPageItemDrawdownAuthorizationReview
					{...currentAssignmentItem}
					{...commonProps}
					wire={resource as WirePayment}
					account={account}
					customer={customer}
					wireDrawdownCreationDate={wireDrawdown?.attributes.createdAt}
					businessApplication={businessApplication}
					claims={claims}
				/>
			)
		}
		case AssignmentType.nameMismatchAssignment: {
			const account = included && (included.find((i) => isDepositAccount(i)) as Account)
			const incomingWire = getIncomingWireFromIncluded(included)
			const wirePayment = isWirePayment(resource) ? resource : undefined
			const counterparty = incomingWire?.attributes.counterparty || wirePayment?.attributes.counterparty

			return (
				<AssignmentPageItemNameMismatch
					{...currentAssignmentItem}
					{...commonProps}
					wireReceivedPayment={resource as WireReceivedPayment}
					account={account}
					counterparty={counterparty}
				/>
			)
		}

		default:
			return <div>Unknown Assignment Type</div>
	}
}

export default function AssignmentsView({
	resource,
	included,
	payload,
	assignments,
	accessToken,
	refresh,
	refreshToken,
	claims,
	isCollapsibleQueue,
	defaultCollapsed = false,
}: AssignmentsViewProps) {
	const navigate = useNavigate()
	const {pathname} = useLocation()
	const listPath = useListReturnPath(pageFallbackPath(pathname))

	const [currentAssignmentItemQuery, setCurrentAssignmentItemQuery] = useQueryState<AssignmentItemId | "">(
		"assignmentId",
		""
	)
	const [isQueueCollapsed, setIsQueueCollapsed] = useState(defaultCollapsed)
	const [currentAssignmentItem, setCurrentAssignmentItem] = useState<AssignmentItem | undefined>()

	useEffect(() => {
		const storedIdExistsInAssignments =
			currentAssignmentItemQuery && assignments?.some((assignment) => assignment.id === currentAssignmentItemQuery)
		let defaultItemId: AssignmentItemId | undefined = storedIdExistsInAssignments
			? currentAssignmentItemQuery
			: undefined

		if (!defaultItemId) {
			const firstEnabledOwnersItem = assignments?.find(
				({stage, isOwnersItem}) => isOwnersItem && !getAssignmentIsDisabled(stage, true)
			)
			const firstPendingItem = assignments?.find(({stage}) => stage === AssignmentStage.Pending)
			defaultItemId = (firstEnabledOwnersItem ?? firstPendingItem ?? assignments?.[0])?.id
			setCurrentAssignmentItemQuery(defaultItemId ?? "")
		}
		const defaultAssignmentItemByQuery = assignments?.find((assignment) => assignment.id === defaultItemId) ?? undefined
		setCurrentAssignmentItem(defaultAssignmentItemByQuery)
	}, [])

	const navigatorRef = useRef<HTMLDivElement>(null)
	const onClick = (assignmentItemId: AssignmentItemId) => {
		setCurrentAssignmentItemQuery(assignmentItemId)
		const item = assignments?.find((assignment) => assignment.id === assignmentItemId)
		if (!item) {
			console.error("Assignment not found")
			return
		}
		setCurrentAssignmentItem(item)
	}
	const showCollapsedQueue = isCollapsibleQueue && isQueueCollapsed

	const onCheckDepositFinalActionSuccess = resource.type === "checkDeposit" ? () => navigate(listPath) : undefined

	return (
		<div className={classNames("assignments-container", {"is-queue-collapsed": isQueueCollapsed})}>
			<GridContainer columns={2} rows={1} gap="0" className="assignments-grid has-full-height">
				<div
					className={classNames("assignments-navigator-container", {
						"scrollable-container": !isQueueCollapsed,
						"is-collapsible": isCollapsibleQueue,
					})}
					ref={navigatorRef}
				>
					{isCollapsibleQueue && (
						<AssignmentsQueueHeader
							isCollapsed={isQueueCollapsed}
							onToggle={() => setIsQueueCollapsed((prev) => !prev)}
						/>
					)}
					{showCollapsedQueue ? (
						<CollapsedAssignmentQueue
							assignments={assignments ?? []}
							selectedId={currentAssignmentItem?.id}
							onSelect={onClick}
						/>
					) : (
						<AssignmentNavigator
							assignments={assignments ?? []}
							groupBy="stage"
							selectedId={currentAssignmentItem?.id}
							onClick={onClick}
							navigatorRef={navigatorRef}
						/>
					)}
				</div>
				<div className="scrollable-container">
					{currentAssignmentItem ? (
						<AssignmentPageItemView
							currentAssignmentItem={currentAssignmentItem}
							resource={resource}
							included={included}
							payload={payload}
							accessToken={accessToken}
							refresh={refresh}
							refreshToken={refreshToken}
							claims={claims}
							onFinalActionSuccess={onCheckDepositFinalActionSuccess}
						/>
					) : (
						<div className="assignments-empty-state">
							<AssignmentsEmptyStateGraphics />
							<span>No items to review</span>
						</div>
					)}
				</div>
			</GridContainer>
		</div>
	)
}
